import { DataTable } from "@/components/data-table/DataTable";
import { Form } from "@/components/form/form";
import { OrganizationSemestersComboboxFormField } from "@/components/form/form-field/combobox/organization-semesters-combobox-form-field";
import {
  getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions,
  useAdminOrganizationGetCurrentAndActiveSemester,
} from "@/orval/admin-organization";
import { adminPaymentsGetPayments } from "@/orval/admin-payments";
import { createFileRoute } from "@tanstack/react-router";
import { PaymentDialog } from "./-components/payment-dialog";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { PortalPageHeader } from "@/components/portal-page-header";
import { optionalSemesterSearchSchema } from "@/lib/route-search";
import { getPaymentColumns } from "./-components/payment-columns";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/payments")({
  component: RouteComponent,
  validateSearch: optionalSemesterSearchSchema,
  loader: async ({ params, location, ...loaderContext }) => {
    const search = optionalSemesterSearchSchema.parse(location.search);
    let semesterId = search.semesterId;
    if (semesterId === undefined) {
      const semesters = await loaderContext.context.queryClient.ensureQueryData(
        getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions(params.organizationId),
      );
      semesterId = semesters?.nextSemesterId ?? semesters?.activeSemesterId ?? undefined;
    }

    if (!semesterId) {
      return;
    }

    const queryOptions = buildDataTableQueryOptions({});
    return ensureRouteQueriesData(loaderContext, [
      {
        queryKey: ["admin-payments", params.organizationId, semesterId, queryOptions, []],
        queryFn: () => adminPaymentsGetPayments(params.organizationId, semesterId, [], queryOptions),
      },
    ]);
  },
});

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | undefined>(undefined);

  const { data: currentAndNextSemester } = useAdminOrganizationGetCurrentAndActiveSemester(organizationId);

  const semesterId =
    (search.semesterId ?? currentAndNextSemester?.nextSemesterId ?? currentAndNextSemester?.activeSemesterId) ||
    undefined;

  const defaultFormValues = {
    ...search,
    semesterId,
  };

  const getPayments = adminPaymentsGetPayments;
  const columns = getPaymentColumns(organizationId);

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Payments"
        actions={
          <Button onClick={() => setIsPaymentDialogOpen(true)}>
            <PlusIcon className="size-4 mr-2" />
            Add Payment
          </Button>
        }
      />

      <DataTable
        columns={columns}
        renderFilters={
          <Form<{ semesterId?: number }>
            useFormProps={{ defaultValues: defaultFormValues }}
            onChange={(data) => navigate({ to: ".", search: data })}
            containerClassName="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:gap-4 md:gap-y-0"
          >
            <OrganizationSemestersComboboxFormField
              className="w-full md:w-80"
              name="semesterId"
              organizationId={organizationId}
            />
          </Form>
        }
        missingRequiredFilterValues={!semesterId}
        missingRequiredFilterValuesMessage="Please select a semester."
        queryFn={(queryOptions, signal, filters) =>
          getPayments(organizationId, semesterId!, filters ?? [], queryOptions, signal)
        }
        queryKeyFn={(params, filters) => ["admin-payments", organizationId, semesterId!, params, filters]}
        onRowClick={(row) => {
          setSelectedPaymentId(row.id);
          setIsPaymentDialogOpen(true);
        }}
        getRowIdFn={(row) => row.id.toString()}
      />

      {semesterId && (
        <PaymentDialog
          organizationId={organizationId}
          semesterId={semesterId}
          isOpen={isPaymentDialogOpen}
          onClose={() => {
            setIsPaymentDialogOpen(false);
            setSelectedPaymentId(undefined);
          }}
          paymentId={selectedPaymentId}
        />
      )}
    </div>
  );
}
